/* eslint-disable unicorn/custom-error-definition */
import { HttpStatus } from '@nestjs/common';
import {
  EmployeeDto,
  EnrollmentStatus,
  InsuredPeopleDto,
  OnboardingPeriodReportDto,
} from '@zorro/clients';

export class ZorroError extends Error {
  override message: string;
  status: number;
  override cause?: unknown;

  constructor(message?: string, status?: number, cause?: unknown) {
    super(message, cause !== undefined ? { cause } : undefined);
    this.name = this.constructor.name;
    this.message = message ?? this.name;
    this.status = status ?? HttpStatus.BAD_REQUEST;
    this.cause = cause;
  }
}

export class InvalidStateError extends ZorroError {}

export enum OtherCoveragesMetlife {
  PET = 'PET',
}

export enum ValidationIndicatorState {
  validating = 'validating',
  valid = 'valid',
  invalid = 'invalid',
}

export enum OtherCoveragesAflac {
  FRAUD_PROTECTION = 'FRAUD_PROTECTION',
  FINANCIAL_AND_LEGAL_HEALTH = 'FINANCIAL_AND_LEGAL_HEALTH',
  ASSISTANCE_PROGRAM_FOR_MENTAL_HEALTH = 'ASSISTANCE_PROGRAM_FOR_MENTAL_HEALTH',
  OTHER = 'OTHER',
}

export enum SubEntityVariant {
  subEntity = 'subEntity',
  subEntity2 = 'subEntity2',
}

export enum YesNo {
  YES = 'YES',
  NO = 'NO',
}

export enum AnvilTemplatePrefix {
  APPLICATION = 'APPLICATION',
  ICHRA = 'ICHRA',
}

export interface MultiSelectData {
  value: string;
  label: string;
  location?: string;
  specialty?: string;
  term?: string;
  medID?: number | null;
}

export enum Permissions {
  NONE = 'NONE',
  /*
   * Grouped by Frontegg "Category"
   * Don Employer
   */
  VIEW_EMPLOYER_OBJECTS = 'VIEW_EMPLOYER_OBJECTS',
  // Elena Employee
  VIEW_SELF = 'VIEW_SELF',
  VIEW_SELF_EMPLOYER_PUBLIC_INFO = 'VIEW_SELF_EMPLOYER_PUBLIC_INFO',
  VIEW_SELF_EMPLOYER_NON_SPECIFIC_BENEFITS_MODEL = 'VIEW_SELF_EMPLOYER_NON_SPECIFIC_BENEFITS_MODEL',
  VIEW_SELF_EMPLOYER_DOCUMENTS = 'VIEW_SELF_EMPLOYER_DOCUMENTS',
  WRITE_SELF = 'WRITE_SELF',
  // Prospecting
  VIEW_QUOTES = 'VIEW_QUOTES',
  EDIT_QUOTES = 'EDIT_QUOTES',
  // Agency Management
  AGENCY_MANAGEMENT = 'AGENCY_MANAGEMENT',
}

type ZorroPermissions = Permissions;

export interface Authorization {
  tenantId: string;
  userId: string;
  roles: string[];
  permissions: ZorroPermissions[];
  isInternal: boolean;
}

export interface Person {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
}

export interface User {
  id: string;
  email: string;
  tenantId: string;
}

export interface Employee extends Person {
  id: string;
  userId: string;
  employerId: string;
  phone?: string | null;
  email: string;
  personalEmail?: string | null;
  class?: string | null;
  zipCode?: string | null;
  salary?: number | null;
  activePeriodId?: string;
}

export interface UserRoles {
  roles: string[];
}

export enum DrugCoverageTier {
  generic = 'generic',
  specialty = 'specialty',
  brand = 'brand',
  preferredBrand = 'preferredBrand',
  preferredGeneric = 'preferredGeneric',
  preferredSpecialty = 'preferredSpecialty',
  nonPreferredBrand = 'nonPreferredBrand',
  nonPreferredGeneric = 'nonPreferredGeneric',
  nonPreferredSpecialty = 'nonPreferredSpecialty',
  notCovered = 'notCovered',
  notListed = 'notListed',
  nonListed = 'nonListed',
  noCoverage = 'noCoverage',
}

export enum DrugCoverageStatus {
  covered = 'covered',
  notCovered = 'notCovered',
  unknown = 'unknown',
}

export enum MedicalPlanMarket {
  individual = 'individual',
}

export enum ProviderType {
  individual = 'individual',
  organization = 'organization',
}

export type EmployeePageData = {
  employee: EmployeeDto;
  onboardingPeriods: OnboardingPeriodReportDto[];
  selectedOnboardingPeriod: OnboardingPeriodReportDto | null;
  insuredPeople: InsuredPeopleDto | null;
};

export enum EmployerStatus {
  New = 'New',
  Active = 'Active',
  Deactivated = 'Deactivated',
}

export enum TierLevel {
  platinum = 'platinum',
  gold = 'gold',
  silver = 'silver',
  expanded_bronze = 'expanded_bronze', // TODO: this should be combined with 'bronze'
  bronze = 'bronze',
  catastrophic = 'catastrophic',
  none = 'none',
}

export enum EnrollmentEffortTier {
  EASY_ENROLL = 'EASY_ENROLL',
  EXTRA_STEPS = 'EXTRA_STEPS',
  SELF_ENROLL = 'SELF_ENROLL',
  UNKNOWN = 'UNKNOWN',
}

export enum FilterableEnrollmentEffortTier {
  EASY_ENROLL = 'EASY_ENROLL',
  EXTRA_STEPS = 'EXTRA_STEPS',
  SELF_ENROLL = 'SELF_ENROLL',
}

export type EmployeeCountByEnrollmentStatus = {
  [enrollmentStatus in EnrollmentStatus]: number;
};

export type DeepReadonly<T> = {
  readonly [P in keyof T]: T[P] extends Record<string, unknown>
    ? DeepReadonly<T[P]>
    : T[P];
};

// Use this type to extract all methods from a class type
// Example -
//    class Example { foo: () => bar }
//    type ExampleMethods = ExtractClassMethods<typeof Example>
// Expected type -
//    type ExampleMethods = { foo: () => bar; }
export type ExtractClassMethods<T> = T extends new (
  args: infer ConstructorArgs
) => infer InstanceType
  ? {
      [Method in keyof InstanceType]: InstanceType[Method] extends (
        ...args: infer MethodArgs
      ) => infer MethodReturn
        ? (...args: MethodArgs) => MethodReturn
        : never;
    }
  : never;

// This helper type gets the value of a type, behaves similarly to "keyof" keyword but for values.
// type SomeValue = ValueOf<{ foo: number; bar: string }>; // type SomeValue = string | number
export type ValueOf<T> = T[keyof T];

// Extract does not error when a key does not exist in the key list, this solves it.
export type StrictExtract<T, U extends keyof T> = U;
